import { Col, Container, Row, Spinner, Button, Form } from "react-bootstrap";
import Error from "../abstractComponents/error";
import { useParams } from "react-router-dom";
import FileIcon from "../../components/abstractComponents/fileIcon";
import {
  ActionFileType,
  ValidityStatus,
  QualityCheckStatusUI,
  QualityCheckStatusType
} from "../../backend/types";
import {
  getChecksums,
  MAX_FILE_SIZE_FOR_EXTERNAL_UPLOAD_GB
} from "../../helpers/fileHelper";
import { getQualityCheckStatusKeyFromValue } from "../../helpers/stringHelper";
import { useEffect, useState } from "react";
import { useDownloadExternalLinkQuery } from "../../backend/hooks/externalLink/queryGetExternalUploadLinks";

interface ParamsDownload {
  uploadId: string;
}

// Create Download Link, that can be used by external Users
export default function ExternalUploadLink() {
  const uploadId = (useParams() as ParamsDownload).uploadId;

  const { getUploadLink, uploadData, errorUpload, uploadIsLoading } =
    useDownloadExternalLinkQuery();

  const [isLoading, setIsLoading] = useState(false);
  const [filesToBeUploaded, setFilesToBeUploaded] = useState(0);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [filesHashed, setFilesHashed] = useState<any[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [description, setDescription] = useState("");
  const [uncompress, setUncompress] = useState(false);
  const [errorFromUploading, setErrorFromUploading] = useState("");
  const [qualityCheckStatus, setQualityCheckStatus] =
    useState<QualityCheckStatusUI>(QualityCheckStatusUI.NOT_STARTED);
  const [uploadHasFinished, setUploadHasFinished] = useState(false);

  const checkFilesSize = (files: any[]) => {
    const errors: string[] = [];
    files.forEach((file: any) => {
      if (file.size >= MAX_FILE_SIZE_FOR_EXTERNAL_UPLOAD_GB) {
        errors.push(
          `File ${file.file} is too large for external upload. Please ensure all files are under 5 GB.`
        );
      }
    });
    setErrorMessages(errors);
  };

  const saveFiles = (e: any) => {
    setIsLoading(true);
    setErrorMessages([]);
    getChecksums(e).then((array) => {
      checkFilesSize(array);

      setFilesHashed((prevFiles) => {
        return [...prevFiles, ...array];
      });

      //reset the input element
      const inputElement = document.getElementById(
        "formFile"
      ) as HTMLInputElement | null;
      if (inputElement) {
        inputElement.value = "";
      }
      setIsLoading(false);
    });

    setFiles((prevFiles) => {
      return [...prevFiles, ...e["target"]["files"]];
    });
  };

  const removeCurrentFileFromFiles = (fileName: string) => {
    setErrorMessages([]);

    setFiles((prevFiles) => {
      return [...prevFiles.filter((file) => file.name !== fileName)];
    });

    setFilesHashed((prevFiles) => {
      return [...prevFiles.filter((file) => file.file !== fileName)];
    });

    checkFilesSize(filesHashed.filter((file) => file.file !== fileName));
  };

  const uploadFiles = () => {
    const fileList: any[] = [];
    const hashList: any[] = [];
    const actionFileTypes: any[] = [];

    // We first start with the small files
    filesHashed.forEach((file) => {
      fileList.push(file.file);
      hashList.push(file.hash);
      actionFileTypes.push(ActionFileType.Output);
    });

    setFilesToBeUploaded((prevState) => prevState + hashList.length);

    if (fileList.length) {
      getUploadLink({
        variables: {
          id: uploadId,
          absolutePaths: fileList,
          md5sums: hashList,
          qualityCheckStatus:
            getQualityCheckStatusKeyFromValue(qualityCheckStatus),
          actionFileTypes,
          uncompressOnUpload: uncompress,
          description: description
        }
      });
    }
  };

  //Runs after upload links are pulled
  useEffect(() => {
    if (typeof uploadData !== "undefined") {
      setIsLoading(true);
      const promiseArray: any = [];
      uploadData.getExternalUploadLinks?.presignedUrls.forEach(
        async (uploadDataParse: any) => {
          const fields = JSON.parse(uploadDataParse.fields);
          const formData = new FormData();
          Object.entries(fields).forEach((element: any) => {
            const [key, value] = element;
            formData.append(key, value);
          });
          //Attach the proper file to the form
          formData.append(
            "file",
            Array.from(files).find((file) => {
              return fields.key.endsWith(file.name);
            })
          );
          promiseArray.push(
            fetch(uploadDataParse.url, {
              method: "POST",
              body: formData
            }).then(() => {
              setFilesToBeUploaded((prevState) => prevState - 1);
            })
          );
        }
      );

      Promise.all(promiseArray)
        .finally(() => {
          setUploadHasFinished(true);
          setIsLoading(false);
        })
        .catch((err) => {
          setErrorFromUploading("Error From Uploading");
          console.error(err);
          setUploadHasFinished(true);
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadData]);

  function changeQualityCheckedStatus(
    event: React.ChangeEvent<HTMLSelectElement>
  ) {
    event.preventDefault();
    setQualityCheckStatus(
      QualityCheckStatusUI[event.target.value as QualityCheckStatusType]
    );
  }

  return (
    <>
      <Container fluid>
        {(uploadIsLoading || isLoading) && (
          <div className="center_div">
            <Spinner animation="border" className="spinner_color" />
            <p>
              {uploadIsLoading
                ? "Generating Link..."
                : "Uploading... Please do not close the page..."}
            </p>
          </div>
        )}
        {!uploadHasFinished ? (
          <>
            <h3>Welcome to PHIL Upload Utility</h3>
            <Row>
              <Col>
                <form
                  encType="multipart/form-data"
                  method="POST"
                  id="uploadFileForm"
                >
                  <Form.Group className="mb-3">
                    <Form.Label>
                      File <span className="red_form">*</span>
                    </Form.Label>
                    <Form.Control
                      disabled={isLoading}
                      type="file"
                      required
                      name="file"
                      onChange={saveFiles}
                      id="uploadFileInputExternal"
                      multiple
                    />
                  </Form.Group>
                  {filesHashed.map((file) => (
                    <div
                      data-testid="fileOutputAdfFormElement"
                      key={file.file}
                      className={
                        file.willOverwrite
                          ? "drop_comment_file drop_comment_file_invalid"
                          : "drop_comment_file"
                      }
                    >
                      <FileIcon
                        fileName={file.file}
                        overallValidityStatus={ValidityStatus.NotSet}
                        fontSize={"small"}
                      />{" "}
                      {file.file}
                      <Button
                        variant="link"
                        onClick={() => removeCurrentFileFromFiles(file.file)}
                        id="deleteRepliedToCommentButton"
                        className="float-end close_or_remove_button link_as_text"
                      >
                        X
                      </Button>
                    </div>
                  ))}

                  {filesHashed.filter((file: any) => {
                    return file.file.endsWith(".zip");
                  }).length > 0 && (
                    <>
                      <br />
                      <Row>
                        <Col>
                          <Form.Label>Uncompress on Upload? </Form.Label>
                        </Col>
                        <Col>
                          <Form.Check
                            type="checkbox"
                            checked={uncompress}
                            id="uncompressUploadValidCheckbox"
                            onChange={() => setUncompress(!uncompress)}
                          />
                        </Col>
                      </Row>
                    </>
                  )}
                  <br />
                  <Form.Label>Upload Description</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    data-testid="uploadDescriptionFormElement"
                    placeholder="Enter description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    id="uploadDescriptionFormElement"
                  />
                  <br />
                  <Row>
                    <Col xs={6}>
                      <Form.Label>Quality Control Status</Form.Label>
                    </Col>
                    <Col xs={6}>
                      <Form.Select
                        value={String(
                          getQualityCheckStatusKeyFromValue(qualityCheckStatus)
                        )}
                        onChange={changeQualityCheckedStatus}
                        key="upload_quality_controlled"
                        id="uploadQualityControlledFormElement"
                      >
                        {Object.entries(QualityCheckStatusUI).map((element) => (
                          <option key={element[0]} value={element[0]}>
                            {element[1]}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Row>
                  {errorMessages.length > 0 && (
                    <div className="drop_comment_file drop_comment_file_invalid">
                      {errorMessages.map((error, index) => (
                        <div key={index}>{error}</div>
                      ))}
                    </div>
                  )}
                </form>
              </Col>
            </Row>
            <br />
            <br />
            <Row>
              <Col>
                <Button
                  className="float-end"
                  variant="primary"
                  disabled={
                    isLoading ||
                    filesHashed.length === 0 ||
                    filesToBeUploaded > 0 ||
                    errorMessages.length > 0
                  }
                  id="finishUploadButton"
                  onClick={() => {
                    uploadFiles();
                  }}
                >
                  Upload
                </Button>
              </Col>
            </Row>
          </>
        ) : (
          <h1>Upload was Successful!</h1>
        )}
      </Container>
      {(errorUpload || errorFromUploading) && (
        <Error error={errorUpload || errorFromUploading} />
      )}
    </>
  );
}
